import { notification } from 'antd';
import type { AxiosError } from 'axios';
import axios from 'axios';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { CallResult } from '../../../../enum';
import type { ErrorMessage } from '../../../../models/error';
import type { AsyncThunkConfig } from '../../../../models/slice';
import { RaygunErrorHandlerService } from '../../../../service/raygun.service';

const { logError } = RaygunErrorHandlerService();

type ClientNotesSliceType = {
  showNotesPanel: boolean;
  loadingSaveNote: boolean;
};

const initialState: ClientNotesSliceType = {
  showNotesPanel: false,
  loadingSaveNote: false,
};

export type ClientNote = {
  client_id: string;
  clinician_id: string | null;
  details: string;
  priority_id: string;
  case_status_id: string;
  follow_up_by?: Dayjs | null;
  call_start_time?: Dayjs | null;
  call_end_time?: Dayjs | null;
  call_result?: CallResult | null;
};

export const saveClientNote = createAsyncThunk<string, ClientNote, AsyncThunkConfig>(
  'clientNotes/saveClientNote',
  async (clientNote, thunkAPI) => {
    try {
      const response = (await axios.post('v2_intervention_upsert', {
        ...clientNote,
        timestamp: dayjs().toISOString(),
        // Sent as a plain date so it isn't shifted by the client's timezone.
        follow_up_by: clientNote.follow_up_by?.format('YYYY-MM-DD') ?? null,
        call_start_time: clientNote.call_start_time?.toISOString() ?? null,
        call_end_time: clientNote.call_end_time?.toISOString() ?? null,
      })) as string;
      if (response) {
        notification.success({
          title: 'Success',
          description: 'Note saved.',
        });
      }
      return response; // intervention_id
    } catch (e) {
      logError(e, ['clientNotesSlice', 'saveClientNote']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

export const clientNotesSlice = createSlice({
  name: 'clientNotes',
  initialState,
  reducers: {
    setShowNotesPanel: (state, action: PayloadAction<boolean>) => {
      state.showNotesPanel = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(saveClientNote.pending, (state) => {
        state.loadingSaveNote = true;
      })
      .addCase(saveClientNote.fulfilled, (state) => {
        state.loadingSaveNote = false;
        state.showNotesPanel = false;
      })
      .addCase(saveClientNote.rejected, (state) => {
        state.loadingSaveNote = false;
      });
  },
});

export const { setShowNotesPanel } = clientNotesSlice.actions;
